import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import MediaImage from "@/components/MediaImage";
import ShareBar from "@/components/public/ShareBar";
import OrderRequestDialog from "@/components/OrderRequestDialog";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Button } from "@/components/ui/button";
import { STATUS } from "@/components/public/ArtworkCard";
import ArtworkCard from "@/components/public/ArtworkCard";

export default function ArtworkDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { customer } = useCustomerAuth();
  const [art, setArt] = useState(null);
  const [active, setActive] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setRelated([]);
    api.get(`/artworks/${slug}`).then(({ data }) => {
      setArt(data);
      setActive(data.main_image);
      const tag = (data.tags || [])[0];
      if (tag) {
        api.get("/artworks", { params: { tag } }).then(({ data: list }) => {
          setRelated(list.filter((a) => a.slug !== data.slug).slice(0, 3));
        }).catch(() => {});
      }
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="on-dark violet-canvas flex-1 w-full">
        <div className="mx-auto max-w-3xl px-5 py-32 text-center">
          <h1 className="font-serif text-4xl">Kunstwerk nicht gefunden</h1>
          <Link to="/galerie" className="mt-6 inline-block border-b border-[#F4C542] text-[#F4C542]">Zurück zur Galerie</Link>
        </div>
      </div>
    );
  }
  if (!art) return <div className="on-dark violet-canvas flex-1 w-full py-32 text-center text-[#B79FD1]">Laden…</div>;

  const st = STATUS[art.availability] || STATUS.available;
  const gallery = [art.main_image, ...(art.images || [])].filter(Boolean);
  const mainId = active || art.main_image;
  const structured = {
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    name: art.title,
    artist: { "@type": "Person", name: "Susanne Monika Gysin" },
    artMedium: art.material || undefined,
    artform: art.technique || undefined,
    dateCreated: art.year ? String(art.year) : undefined,
  };

  return (
    <div className="on-dark violet-canvas flex-1 w-full">
      <div className="fade-in mx-auto max-w-7xl px-5 sm:px-8 py-8 sm:py-12">
      <SEO title={art.seo_title || art.title} description={art.seo_description || art.description} image={mediaUrl(art.main_image)} type="article" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }} />

      <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-1 text-sm text-[#B79FD1] hover:text-[#F4C542] mb-8">
        <ChevronLeft className="w-4 h-4" /> Zurück
      </button>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="overflow-hidden bg-[#F3E7D2]">
            {mainId ? (
              <img src={mediaUrl(mainId)} alt={art.alt_text || art.title} className="block w-full h-auto" />
            ) : (
              <div className="ph bg-[#F3E7D2] aspect-[4/5] w-full" />
            )}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4 flex-wrap">
              {gallery.map((g) => (
                <button key={g} onClick={() => setActive(g)} className={`w-20 h-20 overflow-hidden bg-[#F3E7D2] ${active === g ? "ring-2 ring-brand" : ""}`}>
                  <MediaImage id={g} thumb alt="" aspect="aspect-square" className="w-full" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:pt-4">
          {art.category && <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-4">{art.category}</div>}
          <h1 className="font-serif text-5xl font-light leading-tight" data-testid="artwork-title">{art.title}</h1>
          <div className={`mt-4 text-xs uppercase tracking-[0.2em] ${st.cls}`} data-testid="artwork-status">{st.label}</div>
          {art.availability === "reserved" && (
            <div className="mt-4 rounded-md bg-[#FFF4D6] border border-[#F4C542]/60 px-4 py-3 text-sm text-[#6E4A00]" data-testid="artwork-reserved-banner">
              Dieses Werk ist aktuell <strong>reserviert</strong>. Eine unverbindliche Anfrage ist weiterhin möglich.
            </div>
          )}

          <dl className="mt-8 space-y-3 text-sm border-t border-[#632A8E]/50 pt-6">
            {art.year && <Row k="Jahr" v={art.year} />}
            {art.technique && <Row k="Technik" v={art.technique} />}
            {art.material && <Row k="Material" v={art.material} />}
            {(art.width || art.height) && <Row k="Abmessungen" v={`${art.width || "?"} × ${art.height || "?"} cm`} />}
            <Row k="Preis" v={art.show_price && art.price != null ? `CHF ${Number(art.price).toLocaleString("de-CH")}` : "Preis auf Anfrage"} />
          </dl>

          {art.description && <p className="mt-8 text-[#D8B9EA] leading-relaxed whitespace-pre-wrap">{art.description}</p>}
          {art.story && (
            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-[#F4C542] mb-3">Zum Werk</div>
              <p className="text-[#D8B9EA] leading-relaxed whitespace-pre-wrap italic">{art.story}</p>
            </div>
          )}

          <div className="mt-10">
            {art.availability === "sold" ? (
              <p className="text-sm text-[#B79FD1] italic" data-testid="artwork-sold-note">Dieses Werk ist bereits verkauft.</p>
            ) : customer ? (
              <OrderRequestDialog
                artwork={art}
                reservedOnly={art.availability === "reserved"}
                trigger={
                  <Button data-testid="order-open-button" className="bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md h-12 px-10 text-sm w-full sm:w-auto">
                    {art.availability === "reserved" ? "Unverbindliche Anfrage senden" : "Anfrage / Bestellanfrage senden"}
                  </Button>
                }
              />
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/anmelden", { state: { from: location } })}
                  data-testid="login-to-inquire-button"
                  className="bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md h-12 px-10 text-sm w-full sm:w-auto"
                >
                  Anmelden für Anfrage
                </Button>
                <p className="text-sm text-[#B79FD1]">
                  Bitte <Link to="/anmelden" state={{ from: location }} className="underline text-[#F4C542]">melde dich an</Link> oder{" "}
                  <Link to="/registrieren" className="underline text-[#F4C542]">registriere dich</Link>, um dieses Werk anzufragen.
                </p>
              </div>
            )}
          </div>

          <ShareBar title={art.title} className="mt-10" />
        </div>
      </div>

      {art.tags?.length > 0 && (
        <div className="mt-12 flex flex-wrap gap-2" data-testid="artwork-tags">
          {art.tags.map((t) => (
            <Link key={t} to={`/galerie?tag=${encodeURIComponent(t)}`} data-testid={`artwork-tag-${t}`}
              className="rounded-full border border-[#632A8E] px-3.5 h-8 inline-flex items-center text-sm text-[#D8B9EA] hover:border-[#F4C542] hover:text-[#F4C542] transition-colors">
              #{t}
            </Link>
          ))}
        </div>
      )}

      {related.length > 0 && (
        <div className="mt-16 pt-10 border-t border-[#632A8E]/50" data-testid="related-artworks">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-6">Verwandte Werke</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {related.map((a, i) => <ArtworkCard key={a.id} art={a} index={i} />)}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-6">
      <dt className="text-[#B79FD1] uppercase tracking-[0.1em] text-xs pt-0.5">{k}</dt>
      <dd className="text-[#FFF9F2] text-right">{v}</dd>
    </div>
  );
}
